import { useMutation, useQueryClient } from 'react-query';
import {
  ConversationMessageType,
  HeroConversation,
  ProductMessageData,
  UploadedFile,
} from '../../model';
import { v4 as uuid } from 'uuid';
import { useGetTwilioConversationById } from './twilioConversationQueries';
import axios from 'axios';

export const useSendTextMessage = (conversation?: HeroConversation) => {
  const { data: twilioConversation } = useGetTwilioConversationById(
    conversation?.twilioConversationId
  );
  return useMutation(
    ['sendTextMessage'],
    ({
      messageBody,
      localMessageId,
    }: {
      messageBody: string;
      localMessageId: string;
    }) => {
      if (!twilioConversation || !conversation) {
        return Promise.resolve(undefined);
      }
      return twilioConversation?.sendMessage(messageBody, {
        type: 'text',
        localMessageId,
        heroConversationId: conversation.id,
      });
    }
  );
};

export const useSendProductMessage = (conversation?: HeroConversation) => {
  const { data: twilioConversation } = useGetTwilioConversationById(
    conversation?.twilioConversationId
  );
  return useMutation(
    ['sendProductMessage'],
    ({
      localMessageId,
      productData,
    }: {
      localMessageId: string;
      productData: ProductMessageData;
    }) => {
      if (!twilioConversation || !conversation) {
        return Promise.resolve(undefined);
      }
      return twilioConversation.sendMessage('', {
        type: ConversationMessageType.Product,
        localMessageId,
        heroConversationId: conversation.id,
        data: productData,
      });
    }
  );
};

export const useSendMediaMessage = (conversation?: HeroConversation) => {
  const { data: twilioConversation } = useGetTwilioConversationById(
    conversation?.twilioConversationId
  );
  return useMutation(
    ['sendMediaMessage'],
    async ({
      localMessageId,
      mediaFile,
    }: {
      localMessageId: string;
      mediaFile: UploadedFile;
    }) => {
      if (!twilioConversation || !conversation) {
        return Promise.resolve(undefined);
      }
      const { data: fileBlob } = await axios.get(mediaFile.url, {
        responseType: 'blob',
      });
      return twilioConversation.sendMessage(
        {
          media: new Blob([fileBlob], { type: mediaFile.type }),
          contentType: mediaFile.type,
        },
        {
          data: { cacheUri: uuid() },
          type: mediaFile.type.includes('video') ? 'video' : 'photo',
          mediaUrl: mediaFile.url,
          heroConversationId: conversation.id,
          localMessageId,
        }
      );
    }
  );
};

export const useSetAllConversationMessagesAsRead = (
  conversationId?: string
) => {
  const queryClient = useQueryClient();
  const { data: conversation } = useGetTwilioConversationById(conversationId);

  return useMutation(
    ['setAllConversationMessagesAsRead'],
    async () => {
      if (conversation && conversation.status === 'joined') {
        await conversation.setAllMessagesRead();
      }
    },
    {
      onSuccess: () => {
        queryClient.setQueryData(
          ['getTwilioConversationUnreadMessagesCount', conversationId],
          0
        );
      },
    }
  );
};
